/* @flow */
import React, {useState} from 'react';
import {useApolloClient, useQuery} from '@apollo/react-hooks';
import {connect} from 'react-redux';
import {pick} from 'lodash';

import {filteredSearchCountQuery} from '../../queries/filteredCountSearchQuery';
import {ResultsParams} from '../../../server/graphql/params';

import type {Element} from 'react';
import type {State as ReduxState} from '../../reducers/index';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import FiltersContainer from '../Filters/FiltersContainer';
import MoreFiltersPanel from '../MoreFiltersPanel/MoreFiltersPanel';
import {searchRequestQueryVariables} from '../../queries/searchRequestQuery';

export type CallbackProps = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number
};

type StateProps = {
    abacus: $PropertyType<ReduxState, 'abacus'>,
    bounds: $PropertyType<$PropertyType<ReduxState, 'map'>, 'bounds'>,
    expandedSearchHasModifiedMapViewport: $PropertyType<
        $PropertyType<ReduxState, 'expandedGroups'>,
        'hasModifiedMapViewport'
    >,
    inputParams: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParams'
    >,
    inputParamsAreChanged: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreChanged'
    >,
    inputParamsAreUpdating: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'inputParamsAreUpdating'
    >,
    params: $PropertyType<$PropertyType<ReduxState, 'searchParams'>, 'params'>,
    pathname: $PropertyType<
        $PropertyType<ReduxState, 'searchParams'>,
        'pathname'
    >,
    system: $PropertyType<ReduxState, 'system'>
};

type CallbackElementType = typeof MoreFiltersPanel | typeof FiltersContainer;

type OwnProps = {
    children: (callbackProps: CallbackProps) => Element<CallbackElementType>,
    searchResultsLoading: boolean
};

type Props = OwnProps &
    StateProps & {
        lazyLoadCounts: boolean
    };

const noData = {results: {filterGroups: [], resultCount: 0}};

const makeSearchVariables = (
    props: Props,
    overrideParams?: $PropertyType<StateProps, 'params'>
) => {
    let isOverridingForFilteredSearchCountQuery = false;

    if (overrideParams) {
        isOverridingForFilteredSearchCountQuery = true;
    } else {
        overrideParams = props.params;
    }

    return searchRequestQueryVariables(
        overrideParams,
        props.abacus,
        props.system,
        isOverridingForFilteredSearchCountQuery
    );
};

/**
 * Builds GraphQL config for searchRequestQuery
 * @param {Props} props
 */
export const searchRequestQueryConfig = (props: Props) => {
    return {
        query: filteredSearchCountQuery,
        variables: makeSearchVariables(props)
    };
};

/**
 * Builds GraphQL config for filteredSearchCountQuery
 * @param {Props} props
 */
export const filterCountQueryConfig = (props: Props) => {
    const {
        bounds,
        expandedSearchHasModifiedMapViewport,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating,
        lazyLoadCounts,
        params,
        system: {searchFilterCounts},
        searchResultsLoading
    } = props;

    let overrideParams = {
        ...pick({...params, ...inputParams}, ResultsParams),
        include: [],
        pageSize: 0
    };

    /* If expanded search has modified the map viewport (expaned search groups has been uncollapsed)
    then we need update the bounding box to include the expanded searches for the filter count. Note here
    we do not use the bounds in the searchParams but from the transative map state */
    if (expandedSearchHasModifiedMapViewport) {
        overrideParams = {...overrideParams, ...bounds};
    }

    return {
        skip:
            searchResultsLoading ||
            inputParamsAreUpdating ||
            (!lazyLoadCounts && !inputParamsAreChanged),
        variables: {
            ...makeSearchVariables(props, overrideParams),
            filterCounts: lazyLoadCounts || searchFilterCounts
        }
    };
};

export const FilteredCountDataProvider = (props: Props) => {
    const {children, searchResultsLoading} = props;

    const client = useApolloClient();

    // get search query from cache
    const searchData = !searchResultsLoading
        ? client.readQuery(searchRequestQueryConfig(props))
        : noData;

    const [cachedData, setCachedData] = useState(searchData || noData);

    // filter counts query
    const {data: filterCountData} = useQuery(filteredSearchCountQuery, {
        ...filterCountQueryConfig(props),
        onCompleted: setCachedData
    });

    const {
        results: {filterGroups, resultCount}
    } = filterCountData || cachedData;
    return children({
        resultsCount: resultCount,
        filterGroups
    });
};

FilteredCountDataProvider.displayName = 'FilteredCountDataProvider';

const mapStateToProps = ({
    abacus,
    expandedGroups: {
        hasModifiedMapViewport: expandedSearchHasModifiedMapViewport
    },
    map: {bounds},
    searchParams: {
        pathname,
        params,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating
    },
    system
}: ReduxState): StateProps => ({
    abacus,
    bounds,
    expandedSearchHasModifiedMapViewport,
    params,
    inputParams,
    inputParamsAreChanged,
    inputParamsAreUpdating,
    pathname,
    system
});

export const ConnectedFilteredCountDataProvider = connect(
    mapStateToProps,
    {}
)(FilteredCountDataProvider);

ConnectedFilteredCountDataProvider.displayName =
    'ConnectedFilteredCountDataProvider';

const ExperimentFilteredCountDataProvider = (props: OwnProps) => {
    return (
        <ConnectedFilteredCountDataProvider lazyLoadCounts={false} {...props} />
    );
};

export default ExperimentFilteredCountDataProvider;
